/**
 * THE SHAPE VOCABULARY — the primitives every plant in this world is made of.
 *
 * Lifted out of `render/vegetation.ts` on 2026-08-21, unchanged, for one
 * structural reason: the near tree builders became `render/tree/grow.ts`, and
 * `grow.ts` needs exactly these functions. With them still in `vegetation.ts`
 * the two modules imported each other, and an ES module cycle only works
 * while neither side touches the other's bindings at module-evaluation time —
 * an invariant nothing checks and any later edit can break. It broke
 * immediately, on a single `void LEAF;` at grow.ts's top level.
 *
 * So they live here, and both consumers import down rather than across.
 * `vegetation.ts` re-exports what it always exported, so nothing outside
 * either file changed.
 *
 * Everything below is the original code and the original comments — the craft
 * in them is `TREES.md`'s, and the reasoning at `part`, `lobe` and `bough` is
 * load-bearing.
 */
import * as THREE from 'three';
import { clamp, hash01, lerp, mulberry32 } from '@/core/rng';

export type Rgb = readonly [number, number, number];

/** Foliage: white, so the instance colour is the plant's colour. */
export const LEAF: Rgb = [1, 1, 1];
/** Wood: the trunk keeps its own tint whatever the canopy is doing. */
export const BARK: Rgb = [0.45, 0.35, 0.28];

export const ATTRS = [['position', 3], ['normal', 3], ['color', 3], ['flex', 1], ['leaf', 1], ['lobe', 3], ['pcen', 3]] as const;

/** Everything merges as triangle soup, so indexed primitives get flattened. */
export function solid(g: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!g.index) return g;
  const flat = g.toNonIndexed();
  g.dispose();
  return flat;
}

/**
 * Finish one part of a plant: paint it, darken it toward its base, and bake
 * its share of the sway. `flex` grows as y² so the root is rigid and the tip
 * is loose — one attribute, and the wind shader needs no per-kind branch.
 *
 * Call this *after* every translate/scale: it reads the part's final y.
 */
export function part(g: THREE.BufferGeometry, colour: Rgb, flex: number, shadeLow = 1,
              /**
               * A tonal multiplier on the whole part, and whether it is foliage
               * (2026-08-18, the hero tree pass). Foliage used to be inferred from
               * the colour being pure LEAF white, which left no room for a lobe
               * to be foliage AND a shade darker than its neighbour — and a
               * canopy of identical greens is a ball however it is shaped. `tone`
               * ×0.72–1.07 is the range the pass uses; `leafy` defaults to the
               * old test so every existing caller means what it meant.
               */
              tone = 1, leafy?: boolean,
              /**
               * The centre of the mass this part is, if it is a round one
               * (2026-08-19). `lobe` carries each vertex's offset from it, and
               * the vertex shader uses that to keep the mass ROUND under the
               * instance's non-uniform scale — `chunk.ts` widens or narrows a
               * whole plant by its `ratio` (0.58–1.38 for a broadleaf, 1.20–
               * 2.25 for an oak), which turns every sphere in a structured
               * crown into a plate or a spindle. The crown's envelope still
               * takes the ratio; the billows in it do not. Zero offset means
               * "not a mass": trunks, limbs, blades, cones all pass through.
               */
              centre?: readonly [number, number, number]): THREE.BufferGeometry {
  const pos = g.attributes.position.array as ArrayLike<number>;
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  const fl = new Float32Array(n);
  const lf = new Float32Array(n);
  if (!g.attributes.lobe) {
    const lb = new Float32Array(n * 3);
    if (centre) for (let i = 0; i < n; i++) {
      lb[i * 3] = pos[i * 3] - centre[0]; lb[i * 3 + 1] = pos[i * 3 + 1] - centre[1]; lb[i * 3 + 2] = pos[i * 3 + 2] - centre[2];
    }
    g.setAttribute('lobe', new THREE.BufferAttribute(lb, 3));
  }
  // Foliage or wood? Only LEAF paints white — the one part colour the
  // instance tint passes through whole. The shader's leaf speckle keys on
  // this, so a trunk never catches the canopy's texture: spotted bark was
  // most of why the first cut of the foliage grain read as disease.
  const isLeaf = leafy ?? (colour[0] >= 0.99 && colour[1] >= 0.99 && colour[2] >= 0.99);
  /**
   * `pcen` — this vertex's offset from its own PART's centroid, for the
   * phenotype density morph (2026-08-19): a foliage mass scales about its own
   * centre by `p + pcen·(k−1)`, which is what lets "denser/sparser foliage"
   * change the crown without moving the wood it hangs on. Written for FOLIAGE
   * parts that stand clear of the ground; wood and anything rooted at y≈0 (a
   * tuft's blades, a cushion's dome, a conifer's grounded skirt) carry zero,
   * so the morph can never lift a grounded thing off its ground or thin a
   * trunk. Computed here from the part's own vertices rather than passed by
   * every builder — a part IS the natural mass unit, and `centre` (the lobe
   * channel) keeps its separate un-stretch meaning.
   */
  if (!g.attributes.pcen) {
    const pc = new Float32Array(n * 3);
    if (isLeaf && n > 0) {
      let cx = 0, cy = 0, cz = 0, minY = Infinity;
      for (let i = 0; i < n; i++) {
        cx += pos[i * 3]; cy += pos[i * 3 + 1]; cz += pos[i * 3 + 2];
        if (pos[i * 3 + 1] < minY) minY = pos[i * 3 + 1];
      }
      cx /= n; cy /= n; cz /= n;
      if (minY > 0.02) for (let i = 0; i < n; i++) {
        pc[i * 3] = pos[i * 3] - cx; pc[i * 3 + 1] = pos[i * 3 + 1] - cy; pc[i * 3 + 2] = pos[i * 3 + 2] - cz;
      }
    }
    g.setAttribute('pcen', new THREE.BufferAttribute(pc, 3));
  }
  for (let i = 0; i < n; i++) {
    const y = clamp(pos[i * 3 + 1], 0, 1);
    const s = lerp(shadeLow, 1, y) * tone;
    col[i * 3] = colour[0] * s;
    col[i * 3 + 1] = colour[1] * s;
    col[i * 3 + 2] = colour[2] * s;
    fl[i] = flex * y * y;
    lf[i] = isLeaf ? 1 : 0;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('flex', new THREE.BufferAttribute(fl, 1));
  g.setAttribute('leaf', new THREE.BufferAttribute(lf, 1));
  return g;
}

/** Non-indexed concatenation — the structures.ts trick, with two more channels. */
export function merge(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  for (const [name, size] of ATTRS) {
    const arrs = parts.map((p) => p.attributes[name].array as Float32Array);
    const all = new Float32Array(arrs.reduce((s, a) => s + a.length, 0));
    let o = 0;
    for (const a of arrs) { all.set(a, o); o += a.length; }
    geo.setAttribute(name, new THREE.BufferAttribute(all, size));
  }
  parts.forEach((p) => p.dispose());
  geo.computeBoundingSphere();
  return geo;
}

/** A cone standing on the ground rather than centred on it. */
export const cone = (r: number, h: number, seg: number, base: number) =>
  solid(new THREE.ConeGeometry(r, h, seg).translate(0, base + h / 2, 0));

/**
 * Faceted: one normal per triangle, so a low-poly form presents PLANES to the
 * sun instead of a smoothed round. `solid()` flattens the index but keeps
 * three's smooth normals; this throws them away and recomputes per face —
 * cheap, and the whole difference between a stylised trunk and a pipe.
 */
export function faceted(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const f = solid(g);
  f.deleteAttribute('normal');
  f.computeVertexNormals();
  return f;
}

/**
 * A LIMB — a tapered, faceted stick from `from` toward the direction given by
 * `yaw` (about +y, 0 = +x) and `elev` (from the horizontal, π/2 = straight
 * up), of length `len`, thick `r0` at the root and `r1` at the tip. Returns the
 * geometry and where its tip is, so foliage can be hung off the branch it
 * grew on rather than scattered around a centre (the hero tree pass, 2026-08-18).
 */
export function limb(from: readonly [number, number, number], yaw: number, elev: number,
              len: number, r0: number, r1: number, seg = 5)
  : { g: THREE.BufferGeometry; tip: [number, number, number] } {
  // open-ended: a limb's root is inside the trunk and its tip inside a lobe,
  // so the caps would be ten triangles nobody sees
  const g = faceted(new THREE.CylinderGeometry(r1, r0, len, seg, 1, true))
    .translate(0, len / 2, 0)
    .rotateZ(-(Math.PI / 2 - elev))     // +y toward +x by (π/2 − elev)
    .rotateY(-yaw)                       // then about y — three's rotateY turns +x toward −z for +angle
    .translate(from[0], from[1], from[2]);
  const ce = Math.cos(elev);
  const tip: [number, number, number] = [
    from[0] + Math.cos(yaw) * ce * len, from[1] + Math.sin(elev) * len, from[2] + Math.sin(yaw) * ce * len];
  return { g, tip };
}

/**
 * A BOUGH — a branch that CURVES, in two or three tapering segments.
 *
 * `limb()` draws one straight stick, and a skeleton made of straight sticks
 * reads as a candelabra however many you add: measured on the 2026-08-18
 * broadleaf, the whole wood of the tree was **82 triangles** — a hexagonal
 * trunk and five stubs that stopped dead in mid-air — and `TREES.md`'s own
 * checkpoint rule says that if the leafless tree is not interesting no
 * foliage will fix it. Nothing about the crown could.
 *
 * A real branch leaves its parent at a shallow angle and turns upward as it
 * thins, because it is reaching for light and shedding the weight that held
 * it down. `curve` is how much elevation it gains over its whole length, in
 * radians; `drift` is how much its bearing wanders. Three segments of five
 * facets is thirty triangles for a primary — against a straight limb's ten,
 * and the twenty are the difference between a stick and a bough.
 *
 * Returns every segment plus the tip and the final direction, so the next
 * order of branching can grow OUT of where this one actually went rather
 * than being scattered near it.
 */
export function bough(
  from: readonly [number, number, number], yaw: number, elev: number,
  len: number, r0: number, r1: number,
  opts: { segments?: number; facets?: number; curve?: number; drift?: number } = {},
): { parts: THREE.BufferGeometry[]; tip: [number, number, number]; yaw: number; elev: number } {
  const n = Math.max(1, opts.segments ?? 3);
  const facets = opts.facets ?? 5;
  const curve = opts.curve ?? 0;
  const drift = opts.drift ?? 0;
  const parts: THREE.BufferGeometry[] = [];
  let at: [number, number, number] = [from[0], from[1], from[2]];
  let y = yaw, e = elev;
  for (let i = 0; i < n; i++) {
    // the taper is geometric, so a bough thins the way wood does rather than
    // linearly — the tip stays fine while the root keeps its mass
    const t0 = i / n, t1 = (i + 1) / n;
    const ra = r0 * Math.pow(r1 / r0, t0);
    const rb = r0 * Math.pow(r1 / r0, t1);
    const L = limb(at, y, e, len / n, ra, rb, facets);
    parts.push(L.g);
    at = L.tip;
    // …and it turns as it goes: up by `curve`, aside by `drift`
    e += curve / n;
    y += drift / n;
  }
  return { parts, tip: at, yaw: y, elev: e };
}

/**
 * ROUGHEN a solid — push every vertex along its own radius by a hash of where
 * it is, so the mass stops being a sphere without costing a triangle.
 *
 * The 08-20 pass got the crown to fuse and it still read as a heap of balls,
 * because that is what it was: twenty identical facets on a perfect radius,
 * forty times over. A leaf mass has no radius. This is `render/rocks.ts`'s own
 * `boulderGeometry` trick, which exists there for the same reason — keying the
 * offset on the vertex's own coordinates means the five or seven triangles
 * meeting at a corner all move together and the shell never cracks, which
 * matters here because `faceted()` has already duplicated every vertex per
 * face by the time anything else could touch it.
 *
 * Free, in the only currency that is scarce: the silhouette gets irregular and
 * the triangle count does not move.
 */
export function roughen(g: THREE.BufferGeometry, amp: number, seed: number): THREE.BufferGeometry {
  const pos = g.attributes.position as THREE.BufferAttribute;
  const a = pos.array as Float32Array;
  for (let i = 0; i < pos.count; i++) {
    const o = i * 3;
    const x = a[o], y = a[o + 1], z = a[o + 2];
    // key on the vertex's own coordinates: duplicates hash alike, so they stay welded
    const kx = Math.round(x * 4096), ky = Math.round(y * 4096), kz = Math.round(z * 4096);
    const h = hash01(kx, ky * 7919 + kz, seed) - 0.5;
    const k = 1 + h * amp;
    a[o] = x * k; a[o + 1] = y * k; a[o + 2] = z * k;
  }
  return g;
}

/**
 * A FOLIAGE LOBE — a faceted mass (icosahedron for the big ones, octahedron
 * for the small, detail 0 both: 20 and 8 triangles) squashed and turned so no
 * two read as the same primitive, at `at`. `tone` is its share of the crown's
 * light: over one for what the sun reaches, under for what hangs beneath.
 */
export function lobe(at: readonly [number, number, number], r: number, squash: number,
              spin: number, tone: number, f: number, shadeLo: number,
              stretch = 1,
              /** keep this mass round under the instance ratio (`lobe` attribute)
               *  — the broadleaf wants that; the oak's canopy wants to STRETCH
               *  with its crown into a wide rounded cap (Griffin, 2026-08-19:
               *  "rounded and wide, not balls") */
              round = true,
              /**
               * WHICH SOLID, and it is a question about ROLE rather than size
               * (2026-08-20, the detail pass).
               *
               * This used to read `r >= 0.11` — a threshold calibrated when a
               * broadleaf's crown was 8–14 masses of r 0.17, where everything
               * under it genuinely was a small hanger-on. Halving the masses
               * to build a finer crown put every one of them under the
               * threshold, and the whole canopy silently became eight-facet
               * octahedra: the crown read as a heap of green diamonds, which
               * is precisely the failure `TREES.md` records as "octahedra read
               * as spikes at this size".
               *
               * A mass that is part of the crown's SILHOUETTE needs twenty
               * facets whatever its radius. A mass buried inside or hanging
               * under one can have eight, because nothing sees its outline.
               * So the caller says which it is, and the old size rule is the
               * default for every caller that never thought about it.
               */
              solid?: 'mass' | 'filler'): THREE.BufferGeometry {
  const big = solid ? solid === 'mass' : r >= 0.11;
  const raw = big ? new THREE.IcosahedronGeometry(r, 0) : new THREE.OctahedronGeometry(r, 0);
  // …and it is not a sphere. `spin` is already this mass's own seed, so the
  // roughening is deterministic and no two masses in a crown wear the same
  // irregularity. Only the ones that carry a silhouette pay for it.
  const g = faceted(big ? roughen(raw, 0.34, (spin * 9973) | 0) : raw)
    .scale(stretch, squash, 1 / Math.sqrt(stretch))
    .rotateY(spin).rotateX((spin * 1.7) % 0.6 - 0.3)
    .translate(at[0], at[1], at[2]);
  return part(g, LEAF, f, shadeLo, tone, true, round ? at : undefined);
}

/**
 * A scatter of leaf-cluster cards over lobe surfaces — the piece texture
 * alone cannot buy. However good the per-pixel grain, a crown whose OUTLINE
 * is a clean polyhedron edge still reads as a solid; foliage is ragged
 * exactly where it meets the sky. Each card is a small double-sided quad
 * seated on (and slightly proud of) a lobe's surface, oriented along its
 * radial normal — inside the crown they add depth, and where they cross the
 * silhouette they break it. Near tier only: at range the clean far shape is
 * the whole point of the far shape.
 *
 * `lobes` rows are [x, y, z, radius, ySquash]. ~4 triangles per card.
 */
export function leafCards(
  lobes: ReadonlyArray<readonly [number, number, number, number, number]>,
  count: number, size: number, seed: number, f: number, shadeLo = 0.6,
): THREE.BufferGeometry {
  const rnd = mulberry32(seed);
  const pos = new Float32Array(count * 24 * 3);
  const nor = new Float32Array(count * 24 * 3);
  const lob = new Float32Array(count * 24 * 3);       // offset from the card's own lobe centre
  let o = 0;
  for (let i = 0; i < count; i++) {
    const L = lobes[i % lobes.length];
    for (let k = 0; k < 24; k++) { lob[o + k * 3] = L[0]; lob[o + k * 3 + 1] = L[1]; lob[o + k * 3 + 2] = L[2]; }
    // a random direction, biased a little upward — leaves chase the light,
    // and the underside keeps more of its clean shaded mass
    let dx = rnd() * 2 - 1, dy = rnd() * 2 - 1 + 0.4, dz = rnd() * 2 - 1;
    const dl = Math.hypot(dx, dy, dz) || 1;
    dx /= dl; dy /= dl; dz /= dl;
    const rr = 0.86 + rnd() * 0.22;            // seated, a few just proud
    const px = L[0] + dx * L[3] * rr;
    const py = L[1] + dy * L[3] * L[4] * rr;
    const pz = L[2] + dz * L[3] * rr;
    // a tangent frame with a random roll
    const ux = rnd() - 0.5, uy = rnd() + 0.6, uz = rnd() - 0.5;
    let t1x = uy * dz - uz * dy, t1y = uz * dx - ux * dz, t1z = ux * dy - uy * dx;
    const t1l = Math.hypot(t1x, t1y, t1z) || 1;
    t1x /= t1l; t1y /= t1l; t1z /= t1l;
    const t2x = dy * t1z - dz * t1y, t2y = dz * t1x - dx * t1z, t2z = dx * t1y - dy * t1x;
    const w = size * (0.7 + rnd() * 0.7), h = size * (0.55 + rnd() * 0.6);
    // not a flat plate: a shallow four-plane cap, peaked a little along the
    // normal, so the cluster catches light on facets the way a leaf mass
    // does — a plate this size read as a shard, and was seen doing it
    const bow = size * (0.45 + rnd() * 0.3);
    const mx = px + dx * bow, my = py + dy * bow, mz = pz + dz * bow;
    const quad: ReadonlyArray<readonly [number, number, number]> = [
      [px - t1x * w - t2x * h, py - t1y * w - t2y * h, pz - t1z * w - t2z * h],
      [px + t1x * w - t2x * h, py + t1y * w - t2y * h, pz + t1z * w - t2z * h],
      [px + t1x * w + t2x * h, py + t1y * w + t2y * h, pz + t1z * w + t2z * h],
      [px - t1x * w + t2x * h, py - t1y * w + t2y * h, pz - t1z * w + t2z * h]];
    for (let s = 0; s < 2; s++) {
      const sign = s === 0 ? 1 : -1;
      for (let e = 0; e < 4; e++) {
        const A = quad[s === 0 ? e : (e + 1) % 4];
        const B = quad[s === 0 ? (e + 1) % 4 : e];
        pos[o] = A[0]; pos[o + 1] = A[1]; pos[o + 2] = A[2];
        nor[o] = dx * sign; nor[o + 1] = dy * sign; nor[o + 2] = dz * sign;
        o += 3;
        pos[o] = B[0]; pos[o + 1] = B[1]; pos[o + 2] = B[2];
        nor[o] = dx * sign; nor[o + 1] = dy * sign; nor[o + 2] = dz * sign;
        o += 3;
        pos[o] = mx; pos[o + 1] = my; pos[o + 2] = mz;
        nor[o] = dx * sign; nor[o + 1] = dy * sign; nor[o + 2] = dz * sign;
        o += 3;
      }
    }
  }
  // the cards ride their lobe: the stored value is the centre, turned into
  // the offset here, so a card stays seated on a mass the shader keeps round
  for (let i = 0; i < lob.length; i++) lob[i] = pos[i] - lob[i];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  g.setAttribute('lobe', new THREE.BufferAttribute(lob, 3));
  g.computeVertexNormals();
  return part(g, LEAF, f, shadeLo);
}
